/**
 * The scan run that reported a finding (release 3.2 telemetry item 3): which
 * device, when the run started and ended, and how it ended. A finding carries
 * the scan id of the batch it arrived in (metadata.scanId); the run that used
 * that scan id is looked up on the controller. A re-scan that reports the same
 * finding again takes it over, so this is the LATEST run that reported it.
 */

import { useEffect, useState } from 'react';
import { History } from 'lucide-react';

import { ApiError } from '@/lib/api';
import { getScanRunForScanId, type EdgeScanRunTrace } from '@/lib/edge-telemetry-api';

type State = { kind: 'loading' } | { kind: 'found'; trace: EdgeScanRunTrace } | { kind: 'none' } | { kind: 'error' };

export function scanRunTraceText(t: EdgeScanRunTrace): string {
  const device = t.device.hostname && t.device.hostname !== t.device.displayName
    ? `${t.device.displayName} (${t.device.hostname})`
    : t.device.displayName;
  const started = t.run.startedAt ? new Date(t.run.startedAt).toLocaleString() : null;
  const ended = new Date(t.run.finishedAt).toLocaleString();
  return `${device}: scan ${started ? `started ${started}, ` : ''}ended ${ended}, ${t.run.outcome}.`;
}

export function ScanRunTrace({ scanId }: { scanId: string | null | undefined }) {
  const [state, setState] = useState<State>({ kind: 'loading' });
  useEffect(() => {
    if (!scanId) return;
    let alive = true;
    getScanRunForScanId(scanId)
      .then((trace) => alive && setState({ kind: 'found', trace }))
      .catch((e: unknown) => alive && setState(e instanceof ApiError && e.status === 404 ? { kind: 'none' } : { kind: 'error' }));
    return () => {
      alive = false;
    };
  }, [scanId]);
  if (!scanId) return null;
  return (
    <div className="glass-lite rounded-md p-3 text-xs space-y-0.5" data-testid="finding-scan-run">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground inline-flex items-center gap-1">
        <History className="h-3 w-3" /> Reported by scan run
      </div>
      {state.kind === 'found' && <div>{scanRunTraceText(state.trace)}</div>}
      {state.kind === 'none' && (
        <div className="text-muted-foreground">
          No run recorded for scan {scanId}: it was sent by an agent older than release 3.2, or by a server-side
          job rather than a device.
        </div>
      )}
      {state.kind === 'error' && <div className="text-muted-foreground">The scan run could not be looked up.</div>}
      {state.kind === 'loading' && <div className="text-muted-foreground">Looking up the run...</div>}
    </div>
  );
}
